/**
 * useFormTab — convenience helper that wraps `openFormTab` from TabsContext.
 *
 * The render function now receives THREE callbacks (replacing the older
 * close/viewList pair):
 *
 *   close()         → tear the tab down completely (used by the X button or
 *                     explicit Cancel actions only).
 *   saved()         → "I saved a voucher". Triggers the parent list refresh
 *                     (onSaved) but **keeps the form tab open** so the user
 *                     can immediately start the next entry. The form itself
 *                     is responsible for clearing its local state.
 *   viewList()      → reveal the parent List tab (open it if missing) WITHOUT
 *                     closing the current form tab. New voucher + List remain
 *                     independent tabs.
 *
 * Migration note: legacy code that wired `onSaved={close}` was closing the
 * tab on every successful save, which (a) hid completion feedback and
 * (b) caused the auto-spawn logic on the list page to re-open the form the
 * next time the user clicked the sidebar — opening "List + New" together.
 * The fix is to switch those call sites to `onSaved={saved}`.
 *
 * Example:
 *   openItemForm({
 *     path: "/items/new",
 *     ...
 *     onSaved: reload,
 *     render: ({ close, saved, viewList }) => (
 *       <ItemForm onClose={close} onSaved={saved} onViewList={viewList} />
 *     ),
 *   });
 */
import { useCallback } from "react";
import { useTabs } from "@/contexts/TabsContext";

export function useFormTab() {
  const { openFormTab, closeTab, revealTab } = useTabs();

  return useCallback(
    ({ path, label, icon, parent, render, onSaved }) => {
      const close = () => {
        if (typeof onSaved === "function") onSaved();
        closeTab(path);
      };
      const saved = () => {
        // Refresh the parent list silently; keep this form tab open so the
        // user can create the next voucher without re-opening anything.
        if (typeof onSaved === "function") onSaved();
      };
      const viewList = () => {
        if (typeof onSaved === "function") onSaved();
        if (parent) revealTab(parent);
      };
      openFormTab({
        path,
        label,
        icon,
        parent,
        render: () => render({ close, saved, viewList }),
      });
    },
    [openFormTab, closeTab, revealTab],
  );
}
